import React, { useCallback, useEffect, useState } from 'react';
import {
  Card,
  Button,
  List,
  Modal,
  Form,
  Input,
  Select,
  Tag,
  Space,
  Typography,
  Skeleton,
  message,
  Popconfirm,
  Empty,
  Timeline,
  Row,
  Col,
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, HistoryOutlined } from '@ant-design/icons';
import ModuleNav from './ModuleNav';
import {
  listAgents,
  createAgent,
  updateAgent,
  deleteAgent,
  listAgentHistory,
  fetchMaskingSettings,
} from '../../services/aiImageMaskingApi';

const { Text, Paragraph } = Typography;
const { TextArea } = Input;
const DEFAULT_SYSTEM_PROMPT =
  'You are an AI designer overlaying textile swatches on furniture images. Match lighting and perspective, avoid warping.';

const Agents = () => {
  const [agents, setAgents] = useState([]);
  const [agentsLoading, setAgentsLoading] = useState(false);
  const [agentModalVisible, setAgentModalVisible] = useState(false);
  const [agentModalLoading, setAgentModalLoading] = useState(false);
  const [editingAgent, setEditingAgent] = useState(null);
  const [agentHistoryVisible, setAgentHistoryVisible] = useState(false);
  const [agentHistoryLoading, setAgentHistoryLoading] = useState(false);
  const [agentHistoryRecords, setAgentHistoryRecords] = useState([]);
  const [agentHistoryName, setAgentHistoryName] = useState('');
  const [defaultSystemPrompt, setDefaultSystemPrompt] = useState(DEFAULT_SYSTEM_PROMPT);
  const [defaultAiEngine, setDefaultAiEngine] = useState('openai');
  const [agentForm] = Form.useForm();

  const loadAgents = useCallback(async () => {
    setAgentsLoading(true);
    try {
      const data = await listAgents();
      setAgents(Array.isArray(data?.items) ? data.items : []);
    } catch (error) {
      message.error(error.message || 'Unable to load agents');
    } finally {
      setAgentsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAgents();
  }, [loadAgents]);

  useEffect(() => {
    const loadDefaults = async () => {
      try {
        const data = await fetchMaskingSettings();
        setDefaultSystemPrompt(data?.system_prompt || DEFAULT_SYSTEM_PROMPT);
      setDefaultAiEngine((data?.default_ai_engine || 'openai').toLowerCase());
      } catch (error) {
        // best effort; keep default
      }
    };
    loadDefaults();
  }, []);

  const handleAgentModalOpen = (agent = null) => {
    setEditingAgent(agent);
    agentForm.setFieldsValue({
      agentName: agent?.agent_name || '',
      jobDescription: agent?.job_description || '',
      systemPrompt: agent?.system_prompt || defaultSystemPrompt,
      tags: agent?.tags || [],
      aiEngine: agent?.ai_engine
        ? (agent.ai_engine || '').toLowerCase()
        : 'inherit',
    });
    setAgentModalVisible(true);
  };

  const handleAgentModalCancel = () => {
    setAgentModalVisible(false);
    setEditingAgent(null);
    agentForm.resetFields();
  };

  const handleAgentModalSubmit = async () => {
    try {
      const values = await agentForm.validateFields();
      setAgentModalLoading(true);
      const tagsInput = Array.isArray(values.tags) ? values.tags : (values.tags || '').split(',');
      const tags = tagsInput.map((tag) => tag.trim()).filter((tag) => tag.length > 0);
      const uniqueTags = Array.from(new Set(tags.map((tag) => tag.toLowerCase())))
        .sort((a, b) => a.localeCompare(b))
        .map((key) => tags.find((tag) => tag.toLowerCase() === key));
      const payload = {
        agent_name: values.agentName,
        job_description: values.jobDescription,
        system_prompt: values.systemPrompt,
        tags: uniqueTags,
        ai_engine: values.aiEngine === 'inherit' ? '' : values.aiEngine || defaultAiEngine || 'openai',
      };
      if (editingAgent) {
        await updateAgent(editingAgent.id, payload);
        message.success('Agent updated');
      } else {
        await createAgent(payload);
        message.success('Agent created');
      }
      handleAgentModalCancel();
      loadAgents();
    } catch (error) {
      if (error?.errorFields) {
        return;
      }
      message.error(error.message || 'Unable to save agent');
    } finally {
      setAgentModalLoading(false);
    }
  };

  const handleAgentDelete = async (agent) => {
    try {
      await deleteAgent(agent.id);
      message.success('Agent deleted');
      loadAgents();
    } catch (error) {
      message.error(error.message || 'Unable to delete agent');
    }
  };

  const handleAgentHistory = async (agent) => {
    setAgentHistoryVisible(true);
    setAgentHistoryLoading(true);
    setAgentHistoryName(agent.agent_name);
    try {
      const data = await listAgentHistory(agent.id);
      setAgentHistoryRecords(Array.isArray(data?.items) ? data.items : []);
    } catch (error) {
      message.error(error.message || 'Unable to load agent history');
      setAgentHistoryRecords([]);
    } finally {
      setAgentHistoryLoading(false);
    }
  };

  return (
    <div className="ai-image-masking-page">
      <ModuleNav activeKey="agents" />

      <Row gutter={[24, 24]}>
        <Col span={24}>
          <Card
            title="Ai Tryout Agents"
            className="shadow-sm"
            bodyStyle={{ padding: '24px' }}
            extra={
              <Button type="primary" icon={<PlusOutlined />} onClick={() => handleAgentModalOpen()}>
                New Agent
              </Button>
            }
          >
            {agentsLoading ? (
              <Skeleton active paragraph={{ rows: 4 }} />
            ) : (
              <List
                dataSource={agents}
                locale={{
                  emptyText: 'No agents created yet',
                }}
                renderItem={(item) => (
                  <List.Item
                    actions={[
                      <Button
                        size="small"
                        icon={<EditOutlined />}
                        onClick={() => handleAgentModalOpen(item)}
                        key="edit"
                      >
                        Edit
                      </Button>,
                      <Button
                        size="small"
                        icon={<HistoryOutlined />}
                        onClick={() => handleAgentHistory(item)}
                        key="history"
                      >
                        History
                      </Button>,
                      <Popconfirm
                        title="Delete agent?"
                        description="This removes the agent configuration."
                        okText="Delete"
                        okButtonProps={{ danger: true }}
                        onConfirm={() => handleAgentDelete(item)}
                        key="delete"
                      >
                        <Button size="small" danger icon={<DeleteOutlined />}>
                          Delete
                        </Button>
                      </Popconfirm>,
                    ]}
                  >
                    <List.Item.Meta
                      title={
                        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                          <Text strong>{item.agent_name}</Text>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {item.updated_at ? new Date(item.updated_at).toLocaleString() : '--'}
                          </Text>
                        </div>
                      }
                      description={
                        <Space direction="vertical" size={4} className="w-100">
                          <Text type="secondary">{item.job_description}</Text>
                          {item.tags && item.tags.length > 0 ? (
                            <Space size="small" wrap>
                              {item.tags
                                .slice()
                                .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
                                .map((tag) => (
                                  <Tag key={`${item.id}-${tag}`}>{tag}</Tag>
                                ))}
                            </Space>
                          ) : (
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              No tags
                            </Text>
                          )}
                          <Paragraph ellipsis={{ rows: 2 }} type="secondary" className="mb-0">
                            {item.system_prompt}
                          </Paragraph>
                        </Space>
                      }
                    />
                  </List.Item>
                )}
              />
            )}
          </Card>
        </Col>
      </Row>

      <Modal
        title={editingAgent ? 'Edit Agent' : 'New Agent'}
        open={agentModalVisible}
        onCancel={handleAgentModalCancel}
        onOk={handleAgentModalSubmit}
        confirmLoading={agentModalLoading}
        destroyOnClose
        width={720}
      >
        <Form layout="vertical" form={agentForm} requiredMark={false}>
          <Row gutter={[16, 16]}>
            <Col xs={24} md={12}>
              <Form.Item
                label="Agent Name"
                name="agentName"
                rules={[
                  { required: true, message: 'Agent name is required' },
                  { max: 60, message: 'Keep agent name under 60 characters' },
                ]}
              >
                <Input placeholder="e.g., Lifestyle Overlay Specialist" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label="Job Description"
                name="jobDescription"
                rules={[{ required: true, message: 'Job description is required' }]}
              >
                <Input placeholder="Summarize this agent's responsibility" />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item label="Tags" name="tags">
                <Select mode="tags" placeholder="Add tags (press Enter or comma to separate)" tokenSeparators={[',']} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="AI Engine" name="aiEngine" rules={[{ required: true, message: 'Select an AI engine' }]}>
                <Select>
                  <Select.Option value="inherit">Use system default ({defaultAiEngine || 'openai'})</Select.Option>
                  <Select.Option value="openai">OpenAI</Select.Option>
                  <Select.Option value="gemini">Gemini Nano Banana</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                label="System Prompt"
                name="systemPrompt"
                rules={[{ required: true, message: 'System prompt is required' }]}
              >
                <TextArea rows={6} placeholder="Provide a specialized prompt for this agent" />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      <Modal
        title={`Agent History · ${agentHistoryName || ''}`}
        open={agentHistoryVisible}
        onCancel={() => setAgentHistoryVisible(false)}
        footer={null}
        destroyOnClose
        width={720}
      >
        {agentHistoryLoading ? (
          <Skeleton active paragraph={{ rows: 6 }} />
        ) : agentHistoryRecords.length === 0 ? (
          <Empty description="No history available for this agent" />
        ) : (
          <Timeline mode="left">
            {agentHistoryRecords.map((entry) => (
              <Timeline.Item key={entry.id}>
                <Space direction="vertical" className="w-100">
                  <div className="d-flex justify-content-between flex-wrap gap-2">
                    <Text strong>{entry.changed_at ? new Date(entry.changed_at).toLocaleString() : ''}</Text>
                    <Text type="secondary">Entry #{entry.id}</Text>
                  </div>
                  <pre
                    style={{
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      backgroundColor: '#f5f5f5',
                      padding: '12px',
                      borderRadius: 8,
                      marginBottom: 0,
                    }}
                  >
                    {JSON.stringify(entry.changed_fields, null, 2)}
                  </pre>
                </Space>
              </Timeline.Item>
            ))}
          </Timeline>
        )}
      </Modal>
    </div>
  );
};

export default Agents;

